import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, ExternalLink } from "lucide-react";

const CARRIERS = ["UPS", "FedEx", "USPS", "DHL", "XPO Logistics", "Estes", "R+L Carriers", "Old Dominion", "Other"];

// ID-048 RC-CS-SHP-005: tracking before shipping; delivery only after the dealer confirms receipt
export function ShipmentPanel({ req, busy, act }) {
  const t = req.tracking;
  const [f, setF] = useState({ carrier: "", carrier_other: "", tracking_number: "", tracking_url: "" });
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const ready = f.carrier && f.tracking_number.trim() && (f.carrier !== "Other" || f.carrier_other.trim());
  const save = () => act(() => api.post(`/admin/custom-studio/${req.id}/shipment`, f),
    req.status === "SHIPPED" ? "Tracking updated" : "Tracking saved — order marked shipped");
  return (
    <div className="rounded-md border border-terracotta/40 bg-white p-6" data-testid="custom-shipment-panel">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-warmgrey"><Truck className="h-4 w-4 text-terracotta" /> Production shipment</p>
      {t && (
        <div className="mt-3 rounded-md bg-sand/40 p-3 text-sm text-charcoal" data-testid="shipment-current">
          <p>{t.carrier} · <span className="num" data-testid="shipment-current-number">{t.tracking_number}</span></p>
          {t.tracking_url && <a href={t.tracking_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-terracotta underline" data-testid="shipment-current-link">Open tracking <ExternalLink className="h-3 w-3" /></a>}
        </div>
      )}
      <p className="mt-3 text-xs text-warmgrey">{t ? "Correct the tracking details if needed — the earlier entry stays in history." : "Enter the shipment details to mark this order shipped. The dealer sees them on their request page."}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-warmgrey">Carrier</Label>
          <Select value={f.carrier} onValueChange={(v) => setF((x) => ({ ...x, carrier: v }))}>
            <SelectTrigger className="mt-1 h-10 bg-white" data-testid="shipment-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
            <SelectContent>{CARRIERS.map((c) => <SelectItem key={c} value={c} data-testid={`shipment-carrier-${c.toLowerCase().replace(/[^a-z]/g, "")}`}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {f.carrier === "Other" && (
          <div><Label className="text-xs text-warmgrey">Carrier name</Label><Input value={f.carrier_other} onChange={set("carrier_other")} className="mt-1 h-10 bg-white" data-testid="shipment-carrier-other" /></div>
        )}
        <div><Label className="text-xs text-warmgrey">Tracking number</Label><Input value={f.tracking_number} onChange={set("tracking_number")} className="mt-1 h-10 bg-white" data-testid="shipment-tracking-number" /></div>
        <div><Label className="text-xs text-warmgrey">Tracking link (optional)</Label><Input value={f.tracking_url} onChange={set("tracking_url")} placeholder="https://" className="mt-1 h-10 bg-white" data-testid="shipment-tracking-url" /></div>
      </div>
      <Button size="sm" className="mt-3 bg-terracotta text-white hover:bg-terracotta-hover" disabled={busy || !ready} onClick={save} data-testid="shipment-save">
        {t ? "Update tracking" : "Save tracking & mark shipped"}
      </Button>
      {req.status === "SHIPPED" && (
        <div className="mt-6 border-t border-border pt-4" data-testid="delivery-panel">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Delivery</p>
          <label className="mt-2 flex items-center gap-2 text-sm text-charcoal">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="delivery-dealer-confirmed" /> The dealer confirmed they received the order
          </label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. how the dealer confirmed" className="mt-2 bg-white" data-testid="delivery-note" />
          <Button size="sm" className="mt-3 bg-charcoal text-white hover:opacity-90" disabled={busy || !confirmed}
            onClick={() => act(() => api.post(`/admin/custom-studio/${req.id}/delivered`, { dealer_confirmed: true, note }), "Marked delivered — project closed")}
            data-testid="delivery-mark-delivered">Mark delivered & close project</Button>
        </div>
      )}
    </div>
  );
}
